import {
  ItemTypeConsts,
  ItemTypeDisplayNames,
  ItemTypeLabels,
  ItemTypeRoutes,
  getDetailsRoute,
} from 'client/util/Constants';
import pluralize from 'pluralize';

const {
  application,
  configuration,
  configurationGroup,
  device,
  deviceGroup,
  directory,
  ldap,
  policy,
  radiusServer,
  sso,
  systemUser,
  userGroup,
} = ItemTypeConsts;

/**
 * @module ItemType
 */

/**
 * @typedef {Object} ItemType
 * @property {number} id
 * @property {string} label
 * @property {string} title
 * @property {string} labelPlural
 * @property {string} titlePlural
 * @property {string} route
 */

/**
 * This function creates common props for ItemType
 * @param {string} itemType
 * @param {Object} props
 * @returns {ItemType}
 */
function addItemTypeCommonProps(itemType = '', props = {}) {
  const label = ItemTypeLabels[itemType];
  const title = ItemTypeDisplayNames[itemType];

  this.id = props.id || undefined;
  this.label = label;
  this.title = title;
  this.labelPlural = label && pluralize(label);
  this.titlePlural = title && pluralize(title);
  this.route = ItemTypeRoutes[itemType];
  this.type = itemType;

  if (this.id) {
    this.routeDetails = getDetailsRoute(itemType, this.id);
  }
}

/**
 * @typedef {Object} ApplicationItemType
 * @property {string} displayName
 */
/**
 * @typedef {ApplicationItemType & ItemType} ApplicationItemType
 */
/**
 * @param {Object} props
 * @returns {ApplicationItemType}
 */
function ApplicationItemType(props = {}) {
  addItemTypeCommonProps.call(this, application, props);
  this.displayName = props.displayName || '';
}

/**
 * @typedef {ItemType} ConfigurationItemType
 */
/**
 * @param {Object} props
 * @returns {ConfigurationItemType}
 */
function ConfigurationItemType(props = {}) {
  addItemTypeCommonProps.call(this, configuration, props);
}

/**
 * @typedef {ItemType} ConfigurationGroupItemType
 */
/**
 * @param {Object} props
 * @returns {ConfigurationGroupItemType}
 */
function ConfigurationGroupItemType(props = {}) {
  addItemTypeCommonProps.call(this, configurationGroup, props);
}

/**
 * @typedef {Object} IDeviceItemType
 * @property {string} displayName
 * @property {string} os
 */
/**
 * @typedef {IDeviceItemType & ItemType} DeviceItemType
 */
/**
 * @param {Object} props
 * @returns {DeviceItemType}
 */
function DeviceItemType(props = {}) {
  addItemTypeCommonProps.call(this, device, props);
  this.displayName = props.displayName || '';
  this.description = props.description || '';
  this.os = props.os || '';
}

/**
 * @typedef {Object} DeviceGroupItemType
 * @property {string} displayName
 * @property {string} os
 */
/**
 * @typedef {DeviceGroupItemType & ItemType} DeviceGroupItemType
 */
/**
 * @param {Object} props
 * @returns {DeviceGroupItemType}
 */
function DeviceGroupItemType(props = {}) {
  addItemTypeCommonProps.call(this, deviceGroup, props);
  this.displayName = props.displayName || '';
  this.os = props.os || '';
}

/**
 * @typedef {ItemType} DirectoryItemType
 */
/**
 * @param {Object} props
 * @returns {DirectoryItemType}
 */
function DirectoryItemType(props = {}) {
  addItemTypeCommonProps.call(this, directory, props);
}

/**
 * @typedef {ItemType} LdapItemType
 */
/**
 * @param {Object} props
 * @returns {LdapItemType}
 */
function LdapItemType(props = {}) {
  addItemTypeCommonProps.call(this, ldap, props);
}

/**
 * @typedef {Object} PolicyItemType
 * @property {string} displayName
 */
/**
 * @typedef {PolicyItemType & ItemType} PolicyItemType
 */
/**
 * @param {Object} props
 * @returns {PolicyItemType}
 */
function PolicyItemType(props = {}) {
  addItemTypeCommonProps.call(this, policy, props);
  this.displayName = props.displayName || '';
}

/**
 * @typedef {Object} RadiusServerItemType
 * @property {string} displayName
 */
/**
 * @typedef {RadiusServerItemType & ItemType} RadiusServerItemType
 */
/**
 * @param {Object} props
 * @returns {RadiusServerItemType}
 */
function RadiusServerItemType(props = {}) {
  addItemTypeCommonProps.call(this, radiusServer, props);
  this.displayName = props.displayName || '';
}

/**
 * @typedef {ItemType} SsoItemType
 */
/**
 * @param {Object} props
 * @returns {SsoItemType}
 */
function SsoItemType(props = {}) {
  addItemTypeCommonProps.call(this, sso, props);
}

/**
 * @typedef {Object} ISystemUserItemType
 * @property {string} firstname
 * @property {string} lastname
 */
/**
 * @typedef {ISystemUserItemType & ItemType} SystemUserItemType
 */
/**
 * @param {Object} props
 * @returns {SystemUserItemType}
 */
function SystemUserItemType(props = {}) {
  addItemTypeCommonProps.call(this, systemUser, props);
  this.firstname = props.firstname || '';
  this.lastname = props.lastname || '';
}

/**
 * @typedef {Object} UserGroupItemType
 * @property {string} displayName
 */
/**
 * @typedef {UserGroupItemType & ItemType} UserGroupItemType
 */
/**
 * @param {Object} props
 * @returns {UserGroupItemType}
 */
function UserGroupItemType(props = {}) {
  addItemTypeCommonProps.call(this, userGroup, props);
  this.displayName = props.displayName || '';
}

export default {
  ApplicationItemType,
  ConfigurationItemType,
  ConfigurationGroupItemType,
  DeviceItemType,
  DeviceGroupItemType,
  DirectoryItemType,
  LdapItemType,
  PolicyItemType,
  RadiusServerItemType,
  SsoItemType,
  SystemUserItemType,
  UserGroupItemType,
};
